import React, { useEffect, useState, useRef } from 'react';
import Chart from 'chart.js/auto';
// import BackToServicesButton from '../components/BackToServicesButton';
import API_BASE_URL from '../api';
import { authFetch } from '../authFetch';
import { useAuth } from '../authContext';
import useSelectedRadio from '../hooks/useSelectedRadio';
import { resolveCountryFlag } from "../constants/countries";
import { useTranslation } from 'react-i18next';
import { useLocale, fmtNumber } from '../i18n/format';
import ExportButton from '../components/ExportButton';
import { buildNewListenersDoc } from "../pdf/builders/new_listeners/buildDoc";
import { buildNewListenersWorkbook } from "../excel/builders/new_listeners/buildWorkbook";

export default function NewListenersServicePage() {
  const [period, setPeriod] = useState('day');
  const [date1, setDate1] = useState('');
  const [data, setData] = useState([]);
  const [labels, setLabels] = useState([]);
  const [total, setTotal] = useState(0);
  const [geo, setGeo] = useState([]);
  const [info, setInfo] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');

  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  const exportBlockRef = useRef(null);
  const { isAuth } = useAuth();
  const { radioId, shortcode, name: stationName } = useSelectedRadio();
  const { t } = useTranslation('common');
  const locale = useLocale();

  useEffect(() => {
    if (!date1) {
      const today = new Date();
      setDate1(today.toISOString().slice(0, 10));
    }
  }, [date1]);

  const fetchData = async () => {
    if (!isAuth || !radioId || !date1) return;
    setInfo(t('common.loading'));
    try {
      const API = (API_BASE_URL || '').replace(/\/+$/, '');
      const url = `${API}/radios/${encodeURIComponent(radioId)}/metrics/new-listeners?period=${period}&date1=${date1}`;
      const resp = await authFetch(url);
      const json = await resp.json();
      if (json.error) {
        setInfo(json.error);
        setData([]); setLabels([]); setGeo([]); setTotal(0);
        setStart(''); setEnd('');
        return;
      }
      setData(json.data || []);
      setLabels(json.labels || []);
      setTotal(json.total || 0);
      setGeo(json.geo || []);
      setStart(json.start || '');
      setEnd(json.end || '');
      setInfo('');
      setTimeout(() => drawChart(json), 50);
    } catch {
      setInfo(t('common.network_error'));
      setData([]); setLabels([]); setGeo([]); setTotal(0);
      setStart(''); setEnd('');
    }
  };

  function drawChart(resp) {
    if (!Chart || !chartRef.current || !resp.data) return;
    const ctx = chartRef.current.getContext('2d');
    if (chartInstance.current) chartInstance.current.destroy();

    let drawLabels = resp.labels || [];
    let drawData = resp.data || [];

    if (period === 'day') {
      const paddedLabels = Array(7).fill('');
      const paddedData = Array(7).fill(0);
      paddedLabels[3] = drawLabels[0] || '';
      paddedData[3] = drawData[0] || 0;
      drawLabels = paddedLabels;
      drawData = paddedData;
    }

    chartInstance.current = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: drawLabels,
        datasets: [{
          label: t('new_listeners.chart_label'),
          data: drawData,
          backgroundColor: '#36a2eb'
        }]
      },
      options: {
        responsive: true,
        plugins: { legend: { display: false } },
        scales: { y: { beginAtZero: true } }
      }
    });
  }

  const clean = (value) => String(value ?? '').replace(/[^0-9A-Za-z_-]/g, '-');
  const filenameBase = `new_listeners_${clean(shortcode || 'channel')}_${clean(period || 'period')}_${clean(date1 || '')}`;
  const exportDisabled = !data?.length || !!info;
  const exportButtonStyle = calcExportButtonStyle(exportDisabled);

  // Данные загружаются вручную после нажатия кнопки «Показати»

  if (!radioId) {
    return (
      <div style={widgetStyle}>
        {/* BackToServicesButton removed */}
        <h2 style={{ marginBottom: 8 }}>{t('new_listeners.title')}</h2>
        <div style={{ color: '#e54848', fontWeight: 600, fontSize: 18 }}>
          {t('common.no_channel')}
        </div>
      </div>
    );
  }

  return (
    <div style={widgetStyle}>
      {/* BackToServicesButton removed */}

      {/* Описание сервиса */}
      <div
        style={{
          width: "100%",
          padding: "12px 14px",
          borderRadius: 8,
          background: "#f8f8f8",
          color: "#1a1a1a",
          fontSize: 16.5,
          margin: "8px 0 20px 0",
          boxSizing: "border-box",
          whiteSpace: "pre-line",
          lineHeight: 1.65
        }}
      >
        {t('new_listeners.desc')}
      </div>

      <h2 style={{ marginBottom: 8 }}>{t('new_listeners.title')}</h2>
      <div
        style={{
          marginBottom: 16,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: PERIOD_BUTTON_GAP
        }}
      >
        <div style={{ display: 'inline-flex', gap: PERIOD_BUTTON_GAP }}>
          <button onClick={() => setPeriod('day')} style={periodBtn(period === 'day')}>{t('period.day')}</button>
          <button onClick={() => setPeriod('week')} style={periodBtn(period === 'week')}>{t('period.week')}</button>
          <button onClick={() => setPeriod('month')} style={periodBtn(period === 'month')}>{t('period.month')}</button>
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: DATE_FIELD_GAP,
            marginLeft: BUTTONS_DATE_SPACING,
            flexShrink: 0,
            minWidth: DATE_BLOCK_MIN_WIDTH
          }}
        >
          <span style={{ minWidth: DATE_LABEL_MIN_WIDTH, fontWeight: 500, textAlign: 'right' }}>
            📅 {t('new_listeners.end_date')}
          </span>
          <input type="date" value={date1} onChange={e => setDate1(e.target.value)} style={inputStyle}/>
        </div>
      </div>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: ACTION_BUTTON_GAP,
          alignItems: 'center',
          marginBottom: 16
        }}
      >
        <button
          type="button"
          onClick={fetchData}
          style={showButtonStyle}
        >
          {t('common.show')}
        </button>
        <ExportButton
          data={data}
          onBeforeBuild={async () => {
            const logoUrl = `${process.env.PUBLIC_URL || ''}/Radiomonitor.NetLogo.png`;
            let logoDataUrl = null;
            try {
              const resp = await fetch(logoUrl, { cache: 'no-store' });
              const blob = await resp.blob();
              const reader = new FileReader();
              logoDataUrl = await new Promise((res, rej) => {
                reader.onloadend = () => res(reader.result);
                reader.onerror = rej;
                reader.readAsDataURL(blob);
              });
            } catch {}
            const chartDataUrl =
              (chartRef.current && chartRef.current.toDataURL && chartRef.current.toDataURL("image/png")) || null;
            return { logoDataUrl, chartDataUrl };
          }}
          buildDoc={(payload) =>
            buildNewListenersDoc({
              t, shortcode, stationName,
              serviceName: t('new_listeners.title'),
              period, date1,
              start, end, total, labels, data, geo,
              fmtNumber, locale,
              logoDataUrl: payload?.logoDataUrl || null,
              chartDataUrl: payload?.chartDataUrl || null
            })
          }
          buildWorkbook={(XLSX) =>
            buildNewListenersWorkbook(XLSX, {
              t, shortcode, stationName,
              serviceName: t('new_listeners.title'),
              period, date1,
              start, end, total, labels, data,
              fmtNumber, locale
            })
          }
          filename={`${filenameBase}.pdf`}
          xlsxFilename={`${filenameBase}.xlsx`}
          label={t('export_pdf')}
          labelXlsx={t('export_excel')}
          style={exportButtonStyle}
          disabled={exportDisabled}
        />
      </div>
      <div ref={exportBlockRef}>
        {info && <div style={{ fontWeight: 500, margin: "18px 0", color: 'red' }}>{info}</div>}
        {!info && (
          <>
            <div style={{ fontWeight: 'bold', marginBottom: 10 }}>
              {total > 0
                ? <>
                    {t('new_listeners.total')}: <b>{fmtNumber(total, locale)}</b><br />
                    {t('new_listeners.period')}: {start} — {end}
                  </>
                : t('common.no_data')}
            </div>
            <canvas ref={chartRef} width={500} height={220}
              style={{
                width: '100%',
                height: 220,
                display: 'block',
                border: '1px solid #ddd',
                background: '#fafafa',
                margin: '12px 0',
                borderRadius: 10
              }}
            />
            <div style={{
              fontWeight: 'bold',
              marginTop: 18,
              marginBottom: 7
            }}>🌍 {t('new_listeners.geo_title')}</div>
            <table style={{ width: 300, background: '#fafaff', borderRadius: 16, border: '1px solid #eee', fontSize: '0.98em' }}>
              <thead>
                <tr style={{ background: '#f7f7fa' }}>
                  <th style={thTd}>{t('geo.table.country')}</th>
                  <th style={thTd}>{t('geo.table.count')}</th>
                </tr>
              </thead>
              <tbody>
                {geo.map((row, idx) => {
                  const flag = resolveCountryFlag(row);
                  return (
                    <tr key={idx}>
                      <td style={thTd}>
                        {flag ? (
                          <span>
                            {flag}
                            {" "}
                          </span>
                        ) : null}
                        {row.country}
                      </td>
                      <td style={thTd}>{fmtNumber(row.cnt, locale)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </>
        )}
      </div>
    </div>
  );
}

const widgetStyle = {
  maxWidth: 1020,
  margin: '40px auto',
  background: '#fff',
  borderRadius: 14,
  padding: 24,
  boxShadow: '0 2px 12px 0 #0001'
};

const inputStyle = {
  padding: 7,
  borderRadius: 4,
  border: '1px solid #ccc',
  fontSize: 16,
  minWidth: 130
};

const PERIOD_BUTTON_WIDTH = 110;
const periodBtn = active => ({
  background: active ? '#36a2eb' : '#f4f4fa',
  color: active ? '#fff' : '#222',
  border: 'none',
  padding: '7px 18px',
  borderRadius: 5,
  fontWeight: 600,
  cursor: 'pointer',
  width: PERIOD_BUTTON_WIDTH
});

const ACTION_BUTTON_WIDTH = 215;
const ACTION_BUTTON_GAP = 10;
const PERIOD_BUTTON_GAP = 8;
const DATE_FIELD_GAP = 8;
const BUTTONS_DATE_SPACING = 16;
const DATE_BLOCK_MIN_WIDTH = 260;
const DATE_LABEL_MIN_WIDTH = 130;

const thTd = {
  borderBottom: "1px solid #eee",
  padding: "4px 6px",
  textAlign: "left",
  verticalAlign: "top"
};

const baseActionButtonStyle = {
  ...periodBtn(false),
  width: ACTION_BUTTON_WIDTH
};

const showButtonStyle = {
  ...baseActionButtonStyle,
  background: '#3867fa',
  color: '#fff'
};

const calcExportButtonStyle = (disabled) => ({
  ...baseActionButtonStyle,
  background: disabled ? '#9bbdfd' : '#3867fa',
  color: '#fff',
  cursor: disabled ? 'not-allowed' : baseActionButtonStyle.cursor
});
